import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FranchiseProvider } from "@/context/FranchiseContext";
import { MainLayout, POSLayout } from "@/components/layout";
import { ProtectedRoute, RouteProtectedRoute } from "@/components/ProtectedRoute";
import { 
  Dashboard, POSDashboard, AdminConfig, Workflows, Notifications, AuditLog,
  ReservationsPage, InventoryPage, SupplierPortalPage, LoginPage, FinanceDashboard,
  HRPage, CustomersPage, ApprovalsPage, LicensesPage, AnalyticsPage
} from "@/pages";
import { PreviewBanner } from "@/components/PreviewBanner";
import LandingPage from "@/pages/LandingPage";
import ExpenseManagementPage from "@/pages/ExpenseManagementPage";
import AIAnalyticsDashboard from "@/pages/AIAnalyticsDashboard";

// Kitchen Queue Page (Placeholder for Chef role)
function KitchenQueuePage() {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Kitchen Queue</h1>
      <p className="text-gray-600">Kitchen order management coming soon.</p>
    </div>
  );
}

// Tables Page (Placeholder for FOH Staff)
function TablesPage() {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Table Management</h1>
      <p className="text-gray-600">Table and walk-in management coming soon.</p>
    </div>
  );
}

// Supplier Portal Pages
function SupplierOrdersPage() {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Purchase Orders</h1>
      <p className="text-gray-600">View purchase orders sent to your company.</p>
    </div>
  );
}

function SupplierUploadPage() {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Upload Documents</h1>
      <p className="text-gray-600">Submit invoices and delivery notes.</p>
    </div>
  );
}

// Customer Portal Pages
function CustomerOrderPage() {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Order Online</h1>
      <p className="text-gray-600">Online ordering coming soon.</p>
    </div>
  );
}

function CustomerReservationsPage() {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">My Reservations</h1>
      <p className="text-gray-600">View and manage your reservations.</p>
    </div>
  );
}

function CustomerLoyaltyPage() {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Loyalty & Rewards</h1>
      <p className="text-gray-600">View your loyalty points and rewards.</p>
    </div>
  );
}

function CustomerOrderHistoryPage() {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Order History</h1>
      <p className="text-gray-600">View your past orders.</p>
    </div>
  );
}

// Smart redirect based on role after login
function RoleBasedRedirect() {
  const { isAuthenticated, loading, homeRoute } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <Navigate to={homeRoute} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      
      {/* Smart role-based redirect */}
      <Route path="/home" element={<RoleBasedRedirect />} />
      
      {/* POS - Full screen layout (no sidebar) */}
      <Route element={<ProtectedRoute><POSLayout /></ProtectedRoute>}>
        <Route path="/pos" element={
          <RouteProtectedRoute path="/pos">
            <POSDashboard />
          </RouteProtectedRoute>
        } />
      </Route>
      
      {/* Main Application Layout */}
      <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        {/* Main Dashboard */}
        <Route path="/dashboard" element={
          <RouteProtectedRoute path="/dashboard">
            <Dashboard />
          </RouteProtectedRoute>
        } />
        
        {/* Operations */}
        <Route path="/kitchen" element={
          <RouteProtectedRoute path="/kitchen">
            <KitchenQueuePage />
          </RouteProtectedRoute>
        } />
        <Route path="/tables" element={
          <RouteProtectedRoute path="/tables">
            <TablesPage />
          </RouteProtectedRoute>
        } />
        <Route path="/inventory" element={
          <RouteProtectedRoute path="/inventory">
            <InventoryPage />
          </RouteProtectedRoute>
        } />
        <Route path="/finance" element={
          <RouteProtectedRoute path="/finance">
            <FinanceDashboard />
          </RouteProtectedRoute>
        } />
        <Route path="/expenses" element={
          <RouteProtectedRoute path="/expenses">
            <ExpenseManagementPage />
          </RouteProtectedRoute>
        } />
        
        {/* Management */}
        <Route path="/approvals" element={
          <RouteProtectedRoute path="/approvals">
            <ApprovalsPage />
          </RouteProtectedRoute>
        } />
        <Route path="/hr" element={
          <RouteProtectedRoute path="/hr">
            <HRPage />
          </RouteProtectedRoute>
        } />
        <Route path="/reservations" element={
          <RouteProtectedRoute path="/reservations">
            <ReservationsPage />
          </RouteProtectedRoute>
        } />
        <Route path="/customers" element={
          <RouteProtectedRoute path="/customers">
            <CustomersPage />
          </RouteProtectedRoute>
        } />
        
        {/* Compliance & Suppliers */}
        <Route path="/documents" element={
          <RouteProtectedRoute path="/documents">
            <SupplierPortalPage />
          </RouteProtectedRoute>
        } />
        <Route path="/licenses" element={
          <RouteProtectedRoute path="/licenses">
            <LicensesPage />
          </RouteProtectedRoute>
        } />
        
        {/* Insights */}
        <Route path="/analytics" element={
          <RouteProtectedRoute path="/analytics">
            <AnalyticsPage />
          </RouteProtectedRoute>
        } />
        <Route path="/ai-analytics" element={
          <RouteProtectedRoute path="/ai-analytics">
            <AIAnalyticsDashboard />
          </RouteProtectedRoute>
        } />
        
        {/* System */}
        <Route path="/admin" element={
          <RouteProtectedRoute path="/admin">
            <AdminConfig />
          </RouteProtectedRoute>
        } />
        <Route path="/workflows" element={
          <RouteProtectedRoute path="/workflows">
            <Workflows />
          </RouteProtectedRoute>
        } />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/audit" element={
          <RouteProtectedRoute path="/audit">
            <AuditLog />
          </RouteProtectedRoute>
        } />
        
        {/* Supplier Portal */}
        <Route path="/supplier/orders" element={
          <RouteProtectedRoute path="/supplier/orders">
            <SupplierOrdersPage />
          </RouteProtectedRoute>
        } />
        <Route path="/supplier/upload" element={
          <RouteProtectedRoute path="/supplier/upload">
            <SupplierUploadPage />
          </RouteProtectedRoute>
        } />
        
        {/* Customer Portal */}
        <Route path="/order" element={
          <RouteProtectedRoute path="/order">
            <CustomerOrderPage />
          </RouteProtectedRoute>
        } />
        <Route path="/my-reservations" element={
          <RouteProtectedRoute path="/my-reservations">
            <CustomerReservationsPage />
          </RouteProtectedRoute>
        } />
        <Route path="/loyalty" element={
          <RouteProtectedRoute path="/loyalty">
            <CustomerLoyaltyPage />
          </RouteProtectedRoute>
        } />
        <Route path="/order-history" element={
          <RouteProtectedRoute path="/order-history">
            <CustomerOrderHistoryPage />
          </RouteProtectedRoute>
        } />
      </Route>
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <FranchiseProvider>
            <PreviewBanner />
            <AppRoutes />
          </FranchiseProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
